/* ─────────────────────────────────────────────────────────────
   SolicitudesPipaModule.jsx — DMU 614
   Paso 2: Bandeja UNIFICADA de pendientes de pipa.
   Junta CRC, Oficio, Ciudadano, Interno (H3) y Área (H5) en una sola
   pantalla, ordena por score y permite asignar/reasignar pipa.
   ───────────────────────────────────────────────────────────── */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import ModoDemoBanner from "../components/ModoDemoBanner";
import "../styles/supervisor.css";
import "../styles/modal-parque.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORIGEN_META = {
  // CRC y CIUDADANO se muestran como un solo tipo "CRC" en la UI.
  // Vienen de la app móvil / sistema CRC y para el supervisor son lo mismo.
  CRC:       { label: "CRC",              icon: "fa-square-poll-vertical",   bg: "#FEF3C7", color: "#92400E" },
  OFICIO:    { label: "Oficio",           icon: "fa-file-lines",             bg: "#E0E7FF", color: "#3730A3" },
  CIUDADANO: { label: "CRC",              icon: "fa-square-poll-vertical",   bg: "#FEF3C7", color: "#92400E" },
  INTERNO:   { label: "Reporte Interno",  icon: "fa-wrench",                 bg: "#FFE4E6", color: "#9F1239" },
  AREA:      { label: "Área de gobierno", icon: "fa-circle-check",           bg: "#FED7AA", color: "#9A3412" },
};

// Chips visibles en el header (CRC y CIUDADANO se fusionan).
const ORIGEN_CHIPS = [
  ["CRC_UNIFICADO", { label: "CRC / App",         icon: "fa-square-poll-vertical", bg: "#FEF3C7", color: "#92400E" }],
  ["OFICIO",        ORIGEN_META.OFICIO],
  ["INTERNO",       ORIGEN_META.INTERNO],
  ["AREA",          ORIGEN_META.AREA],
];

const URG_META = {
  ALTA:  { bg: "#FEE2E2", color: "#B91C1C" },
  MEDIA: { bg: "#FEF3C7", color: "#B45309" },
  BAJA:  { bg: "#DCFCE7", color: "#15803D" },
};

const ESTADO_PENDIENTE = new Set(["PENDIENTE_SUPERVISOR", "PENDIENTE", "ABIERTO"]);
const ESTADO_NO_ATENDIDO = new Set(["NO_PUDO_ATENDER"]);
const ESTADO_ATENDIDO = new Set(["ATENDIDO"]);

const fmtFecha = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short" }); }
  catch { return iso; }
};

export default function SolicitudesPipaModule() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [verDemo, setVerDemo] = useState(false);
  const [items, setItems] = useState([]);
  const [resumenPipas, setResumenPipas] = useState(null);
  const [choferes, setChoferes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [incluirAsig, setIncluirAsig] = useState(false);
  const [incluirNoAtendidas, setIncluirNoAtendidas] = useState(false);
  const [filtroOrigen, setFiltroOrigen] = useState("");
  const [filtroUrg, setFiltroUrg] = useState("");
  const [filtroZona, setFiltroZona] = useState("");
  const [filtroFolio, setFiltroFolio] = useState("");
  const [asignar, setAsignar] = useState(null);  // ítem para asignar
  const [choferesEnUso, setChoferesEnUso] = useState(new Set());

  const [conteoNoAtendidas, setConteoNoAtendidas] = useState(null);
  const [conteoAtendidas, setConteoAtendidas] = useState(null);

  const puedeGestionar = ["supervisor_general_pipas", "admin"].includes(user?.rol);
  const puedeEliminar = user?.rol === "admin";

  const eliminarSolicitud = async (item) => {
    if (!puedeEliminar) return;
    const ok = window.confirm(
      `¿Eliminar el folio ${item.folio}? Esta acción no se puede deshacer.`
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/capturista/solicitud/${item.id}`);
      // Quitar la tarjeta sin recargar. Conteos y resumen se recalculan
      // automáticamente por los useMemo derivados de `items`.
      setItems(prev => prev.filter(x => x.id !== item.id));
      toast.success(`Folio ${item.folio} eliminado`);
    } catch {
      toast.error("No se pudo eliminar, intenta de nuevo");
    }
  };

  const cargarConteoNoAtendidas = async () => {
    try {
      const r = await axios.get(`${API}/bandeja-pipas/contador-no-atendidas`);
      setConteoNoAtendidas(r.data.total ?? 0);
    } catch {
      // silencioso — el badge simplemente no aparece
    }
  };

  const cargarConteoAtendidas = async () => {
    try {
      const r = await axios.get(`${API}/bandeja-pipas/contador-atendidas-hoy`);
      setConteoAtendidas(r.data.total ?? 0);
    } catch {
      // silencioso
    }
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        axios.get(`${API}/bandeja-pipas`, { params: { incluir_asignadas: incluirAsig, incluir_no_atendidas: incluirNoAtendidas } }),
        axios.get(`${API}/bandeja-pipas/resumen-pipas`),
        axios.get(`${API}/usuarios`, { params: { rol: "chofer" } }),
        axios.get(`${API}/bandeja-pipas/pipas-en-uso`).catch(() => ({ data: { pipa_ids: [], chofer_ids: [] } })),
      ]);
      setItems(r1.data.items || []);
      setResumenPipas(r2.data || null);
      // Ordenar choferes por nombre
      const chofsOrdenados = (r3.data || []).sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es"));
      setChoferes(chofsOrdenados);
      setChoferesEnUso(new Set(r4.data.chofer_ids || []));
      cargarConteoNoAtendidas();
      cargarConteoAtendidas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar la bandeja");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (puedeGestionar) cargar(); /* eslint-disable-next-line */ }, [incluirAsig, incluirNoAtendidas]);

  // zonas únicas para el filtro
  const zonas = useMemo(() => {
    const s = new Set(items.map(i => i.ubicacion?.zona).filter(Boolean));
    return [...s].sort();
  }, [items]);

  // aplicar filtros en cliente (backend también filtra, pero esto evita ida y vuelta)
  const fq = filtroFolio.trim().toLowerCase();
  const matchOrigen = (i) => {
    if (!filtroOrigen) return true;
    if (filtroOrigen === "CRC_UNIFICADO") return i.origen === "CRC" || i.origen === "CIUDADANO";
    return i.origen === filtroOrigen;
  };
  const visibles = useMemo(() => items.filter(i =>
    matchOrigen(i) &&
    (!filtroUrg    || i.urgencia === filtroUrg) &&
    (!filtroZona   || i.ubicacion?.zona === filtroZona) &&
    (!fq           || (i.folio || "").toLowerCase().includes(fq))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [items, filtroOrigen, filtroUrg, filtroZona, fq]);

  // conteos por origen (sobre TODOS los items cargados, para mostrar arriba).
  // CRC y CIUDADANO se cuentan juntos.
  const conteoOrigen = useMemo(() => {
    const c = { CRC_UNIFICADO: 0, OFICIO: 0, INTERNO: 0, AREA: 0 };
    for (const i of items) {
      if (i.origen === "CRC" || i.origen === "CIUDADANO") c.CRC_UNIFICADO++;
      else if (i.origen in c) c[i.origen]++;
    }
    return c;
  }, [items]);

  return (
    <div className="sup-shell" data-testid="page-solicitudes-pipa">
      <ModoDemoBanner verDemo={verDemo} setVerDemo={setVerDemo} />
      <header className="sup-topbar">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/hub")} className="iconbtn-light" data-testid="btn-back-hub">
            <i className="fa-solid fa-arrow-left" />
          </button>
          <div>
            <div className="sup-title"><i className="fa-solid fa-hand-holding-droplet mr-2"/>Solicitudes de Pipa</div>
            <div className="sup-sub">
              {user?.nombre} · <span className="text-sky-300">{user?.rol}</span> · bandeja unificada
            </div>
          </div>
        </div>
        <button onClick={logout} className="iconbtn-light" data-testid="btn-logout">
          <i className="fa-solid fa-right-from-bracket" />
        </button>
      </header>

      <main className="sup-main">
        {!puedeGestionar ? (
          <div className="text-center py-12 text-slate-500" data-testid="no-permisos">
            <i className="fa-solid fa-lock text-3xl mb-3"/>
            <div>Solo supervisor general y admin pueden gestionar solicitudes de pipa.</div>
          </div>
        ) : (
          <>
            {/* Resumen rápido de pipas */}
            <ResumenPipas data={resumenPipas}/>

            {/* Conteos por origen + filtros. Chips con 0 conteo se ven en gris. */}
            <section className="bandeja-conteo" data-testid="bandeja-conteo">
              {ORIGEN_CHIPS.map(([k, m]) => {
                const active = filtroOrigen === k;
                const n = conteoOrigen[k] || 0;
                const disabled = n === 0;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => { if (!disabled) setFiltroOrigen(active ? "" : k); }}
                    className="bandeja-conteo-chip"
                    data-testid={`chip-origen-${k}`}
                    data-active={active}
                    data-disabled={disabled}
                    disabled={disabled}
                    aria-disabled={disabled}
                    title={disabled ? "Sin solicitudes de este tipo" : `Filtrar por ${m.label}`}
                    style={{
                      background: disabled ? "#F1F5F9" : (active ? m.color : m.bg),
                      color:      disabled ? "#94A3B8" : (active ? "white"  : m.color),
                      borderColor: disabled ? "#CBD5E1" : m.color,
                      cursor:     disabled ? "not-allowed" : "pointer",
                      opacity:    disabled ? 0.7 : 1,
                    }}
                  >
                    <i className={`fa-solid ${m.icon} mr-1.5`}/>
                    <strong>{n}</strong> {m.label}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => { setFiltroOrigen(""); setFiltroUrg(""); setFiltroZona(""); setFiltroFolio(""); }}
                className="bandeja-conteo-chip"
                data-testid="chip-limpiar"
                style={{background:"transparent", color:"#475569", borderColor:"#CBD5E1"}}
              >
                <i className="fa-solid fa-broom mr-1.5"/>Limpiar
              </button>
            </section>

            {/* Filtros secundarios */}
            <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="filtros-secundarios">
              <div className="relative" data-testid="buscador-folio-wrap">
                <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"/>
                <input
                  type="text"
                  value={filtroFolio}
                  onChange={(e) => setFiltroFolio(e.target.value)}
                  placeholder="Buscar por folio (ej. CRC683976)"
                  aria-label="Buscar por folio"
                  data-testid="input-buscar-folio"
                  className="text-sm border border-slate-300 rounded-md pl-8 pr-8 py-1.5 w-64 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                />
                {filtroFolio && (
                  <button
                    type="button"
                    onClick={() => setFiltroFolio("")}
                    aria-label="Limpiar búsqueda"
                    data-testid="btn-limpiar-busqueda"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-xs px-1"
                  >
                    <i className="fa-solid fa-xmark"/>
                  </button>
                )}
              </div>
              <select value={filtroUrg} onChange={(e) => setFiltroUrg(e.target.value)} className="mp-select" style={{maxWidth: 160}} data-testid="filtro-urgencia">
                <option value="">Urgencia: todas</option>
                <option value="ALTA">ALTA</option>
                <option value="MEDIA">MEDIA</option>
                <option value="BAJA">BAJA</option>
              </select>
              <select value={filtroZona} onChange={(e) => setFiltroZona(e.target.value)} className="mp-select" style={{maxWidth: 180}} data-testid="filtro-zona">
                <option value="">Zona: todas</option>
                {zonas.map(z => <option key={z} value={z}>{z}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="label-incluir-asignadas">
                <input type="checkbox" checked={incluirAsig} onChange={(e) => setIncluirAsig(e.target.checked)} data-testid="checkbox-incluir-asignadas"/>
                Incluir asignadas
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer font-medium" style={{color: "#B91C1C"}} data-testid="label-incluir-no-atendidas">
                <input type="checkbox" checked={incluirNoAtendidas} onChange={(e) => setIncluirNoAtendidas(e.target.checked)} data-testid="checkbox-incluir-no-atendidas"/>
                Incluir no atendidas
              </label>
              {conteoNoAtendidas > 0 && (
                <button
                  type="button"
                  data-testid="badge-no-atendidas"
                  onClick={() => setIncluirNoAtendidas(true)}
                  title="Ver solicitudes no atendidas"
                  className="flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border"
                  style={{background: "#FEE2E2", color: "#B91C1C", borderColor: "#FCA5A5", cursor: "pointer"}}
                >
                  <i className="fa-solid fa-triangle-exclamation"/>
                  {conteoNoAtendidas} no atendida{conteoNoAtendidas !== 1 ? "s" : ""}
                </button>
              )}
              {conteoAtendidas > 0 && (
                <span
                  data-testid="badge-atendidas-hoy"
                  title="CRCs/Oficios cerrados hoy por choferes vía bot"
                  className="flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border"
                  style={{background: "#DCFCE7", color: "#15803D", borderColor: "#86EFAC"}}
                >
                  <i className="fa-solid fa-circle-check"/>
                  {conteoAtendidas} atendido{conteoAtendidas !== 1 ? "s" : ""} hoy
                </span>
              )}
              <button className="btn-ghost-sm" onClick={cargar} data-testid="btn-refrescar"><i className="fa-solid fa-rotate mr-1"/>Refrescar</button>
              <span className="text-sm text-slate-500 ml-auto">
                <strong data-testid="total-visibles">{visibles.length}</strong> de {items.length} pendientes · ordenados por score
              </span>
            </div>

            {loading && <div className="text-center py-6 text-sm text-slate-500" data-testid="loading">Cargando…</div>}
            {!loading && visibles.length === 0 && (
              fq ? (
                <div
                  className="text-center py-12 text-slate-500 text-sm"
                  data-testid="msg-sin-resultados-folio"
                >
                  <i className="fa-solid fa-circle-info text-slate-400 mr-1"/>
                  No se encontró el folio
                  <div className="text-xs text-slate-400 mt-1">"{filtroFolio.trim()}"</div>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400 text-sm" data-testid="vacio">
                  <i className="fa-solid fa-mug-hot text-3xl mb-2 block"/>
                  Sin pendientes que coincidan con los filtros.
                </div>
              )
            )}

            <div className="space-y-3" data-testid="lista-bandeja">
              {visibles.map(it => (
                <FilaBandeja
                  key={`${it.origen}-${it.id}`}
                  item={it}
                  onAsignar={() => setAsignar(it)}
                  onEliminar={puedeEliminar ? () => eliminarSolicitud(it) : null}
                />
              ))}
            </div>

            {asignar && (
              <DialogoAsignar
                item={asignar}
                choferes={choferes}
                choferesEnUso={choferesEnUso}
                onClose={() => setAsignar(null)}
                onOk={() => { setAsignar(null); cargar(); }}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}

/* ── Resumen de pipas ─────────────────────────────────────── */
function ResumenPipas({ data }) {
  if (!data) return null;
  const op = data.por_estado?.OPERATIVA || 0;
  const taller = data.por_estado?.EN_TALLER || 0;
  const prestada = data.por_estado?.PRESTADA_A_OTRA_ZONA || 0;
  return (
    <section className="resumen-pipas" data-testid="resumen-pipas">
      <i className="fa-solid fa-truck-droplet text-xl mr-2"/>
      <strong>{data.total}</strong> pipas:
      <span className="resumen-chip" data-testid="resumen-operando">
        <i className="fa-solid fa-circle-check text-emerald-600"/> {op} operando
      </span>
      <span className="resumen-chip" data-testid="resumen-taller">
        <i className="fa-solid fa-screwdriver-wrench text-amber-600"/> {taller} en taller
      </span>
      <span className="resumen-chip" data-testid="resumen-prestadas">
        <i className="fa-solid fa-arrow-right-arrow-left text-sky-600"/> {prestada} prestada{prestada === 1 ? "" : "s"}
      </span>
    </section>
  );
}

/* ── Fila de la bandeja ───────────────────────────────────── */
function FilaBandeja({ item, onAsignar, onEliminar }) {
  const om = ORIGEN_META[item.origen] || ORIGEN_META.CRC;
  const um = URG_META[item.urgencia] || URG_META.MEDIA;
  const ub = item.ubicacion || {};
  const esPendiente = ESTADO_PENDIENTE.has(item.estado);
  const esNoAtendido = ESTADO_NO_ATENDIDO.has(item.estado);
  const esAtendido = ESTADO_ATENDIDO.has(item.estado);
  const esParque = ub.tipo === "PARQUE";
  const lat = ub.lat;
  const lon = ub.lon;
  const tieneCoords = lat !== null && lat !== undefined && lon !== null && lon !== undefined;

  const BtnEliminar = onEliminar ? (
    <button
      type="button"
      onClick={onEliminar}
      data-testid={`btn-eliminar-${item.folio}`}
      title={`Eliminar ${item.folio}`}
      className="text-sm rounded inline-flex items-center gap-1 transition hover:bg-rose-50"
      style={{ border: "1px solid #FCA5A5", color: "#B91C1C", background: "white", padding: "0.45rem 0.8rem", fontWeight: 600 }}
    >
      <i className="fa-solid fa-trash-can"/>Eliminar
    </button>
  ) : null;

  return (
    <article className="bandeja-card" data-testid={`bandeja-row-${item.folio}`} data-origen={item.origen}>
      <header className="flex justify-between items-start gap-3 flex-wrap">
        <div className="flex items-start gap-2 flex-wrap">
          <span className="origen-pill" style={{background: om.bg, color: om.color}} data-testid={`pill-origen-${item.folio}`}>
            <i className={`fa-solid ${om.icon} mr-1`}/>{om.label}
          </span>
          <div>
            <div className="font-mono text-xs text-slate-500">{item.folio}</div>
            <div className="font-semibold text-base leading-tight" data-testid={`ubicacion-${item.folio}`}>
              {esParque ? (
                <><i className="fa-solid fa-tree text-emerald-600 mr-1"/>{ub.espacio_nombre} <span className="font-mono text-xs text-slate-500">({ub.espacio_id_oficial})</span></>
              ) : (
                <><i className="fa-solid fa-location-dot text-rose-500 mr-1"/>{ub.direccion_texto || "—"}{ub.colonia ? <span className="text-sm text-slate-500"> · {ub.colonia}</span> : null}</>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="pill-tiny" style={{background: um.bg, color: um.color, fontWeight: 700}} data-testid={`pill-urgencia-${item.folio}`}>{item.urgencia}</span>
          <span className="pill-tiny" style={{background:"#0F172A", color:"white", fontWeight:700}} title="Score sugerido (mayor=más urgente)" data-testid={`pill-score-${item.folio}`}>score {item.score}</span>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mt-2">
        <div><span className="text-slate-500">Zona:</span> <span className="pill-tiny pill-zone">{ub.zona || "—"}</span></div>
        <div><span className="text-slate-500">Días:</span> <strong>{item.dias_esperando >= 9999 ? "—" : item.dias_esperando}</strong></div>
        <div><span className="text-slate-500">Reportado:</span> <span title={item.reportado_por || "—"}>{item.reportado_por || "—"}</span></div>
        <div><span className="text-slate-500">Fecha:</span> {fmtFecha(item.fecha)}</div>
      </div>

      {/* Botón Ver en mapa — visible en la tarjeta sin expandir */}
      <div className="mt-2">
        {tieneCoords ? (
          <a
            className="link-map"
            target="_blank"
            rel="noreferrer"
            href={`https://maps.google.com/?q=${lat},${lon}`}
            data-testid={`btn-mapa-${item.folio}`}
            title="Abrir en Google Maps"
          >
            <i className="fa-solid fa-location-dot"/> Ver en mapa
          </a>
        ) : (
          <span
            className="link-map link-map-off"
            aria-disabled="true"
            data-testid={`btn-mapa-off-${item.folio}`}
            title="Esta solicitud aún no tiene coordenadas registradas"
          >
            <i className="fa-solid fa-location-dot"/> Sin ubicación
          </span>
        )}
      </div>

      {item.descripcion && (
        <p className="text-sm bg-slate-50 rounded p-2 mt-2 mb-0" data-testid={`desc-${item.folio}`}>{item.descripcion}</p>
      )}

      {/* Estado de asignación */}
      {esPendiente ? (
        <div className="flex gap-2 mt-3 flex-wrap">
          <button className="btn-primary" onClick={onAsignar} data-testid={`btn-asignar-${item.folio}`} style={{padding:"0.45rem 0.9rem", fontSize:"0.85rem"}}>
            <i className="fa-solid fa-user-check mr-1"/>Asignar chofer
          </button>
          {BtnEliminar}
        </div>
      ) : esNoAtendido ? (
        <div className="mt-3" data-testid={`no-atendido-${item.folio}`}>
          <div className="rounded-lg px-3 py-2 mb-2 text-sm" style={{background:"#FEF2F2", border:"1.5px solid #FCA5A5", color:"#991B1B"}}>
            <div className="font-bold mb-0.5"><i className="fa-solid fa-circle-xmark mr-1"/>No se atendió</div>
            {item.motivo_no_pudo && <div className="text-xs opacity-90">{item.motivo_no_pudo}</div>}
            {item.fecha_no_pudo && <div className="text-xs opacity-70 mt-0.5">{fmtFecha(item.fecha_no_pudo)}</div>}
            {(item.intentos_previos?.length > 0) && (
              <div className="text-xs opacity-70 mt-0.5">{item.intentos_previos.length} intento(s) previo(s) registrado(s)</div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={onAsignar}
              data-testid={`btn-reintentar-${item.folio}`}
              style={{padding:"0.45rem 0.9rem", fontSize:"0.85rem", background:"#991B1B", color:"#fff", border:"none", borderRadius:8, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:4}}
            >
              <i className="fa-solid fa-rotate-right"/>Reintentar con otro chofer
            </button>
            {BtnEliminar}
          </div>
        </div>
      ) : esAtendido ? (
        <div className="mt-3" data-testid={`atendido-${item.folio}`}>
          <div className="rounded-lg px-3 py-2 text-sm" style={{background:"#F0FDF4", border:"1.5px solid #86EFAC", color:"#15803D"}}>
            <div className="font-bold mb-0.5"><i className="fa-solid fa-circle-check mr-1"/>Atendido</div>
            {item.chofer_nombre && <div className="text-xs opacity-90">Chofer: <strong>{item.chofer_nombre}</strong></div>}
            {item.actualizado_en && <div className="text-xs opacity-70 mt-0.5">{fmtFecha(item.actualizado_en)}</div>}
            {(item.fotos_atencion_count > 0) && <div className="text-xs opacity-70 mt-0.5">{item.fotos_atencion_count} foto(s) de evidencia</div>}
          </div>
        </div>
      ) : (
        <div className="bandeja-asignada flex items-center gap-2 flex-wrap" data-testid={`asignada-${item.folio}`}>
          <i className="fa-solid fa-check-circle mr-1"/>
          {item.chofer_asignado_nombre
            ? <><i className="fa-solid fa-user mr-1"/><strong>{item.chofer_asignado_nombre}</strong></>
            : <>Pipa <strong>{item.pipa_asignada_id || "—"}</strong></>
          }
          {item.fecha_programada ? <> · programada {item.fecha_programada}</> : null}
          <button className="btn-ghost-sm ml-3" onClick={onAsignar} data-testid={`btn-reasignar-${item.folio}`}>
            <i className="fa-solid fa-rotate mr-1"/>Reasignar
          </button>
          {BtnEliminar}
        </div>
      )}
    </article>
  );
}

/* ── Diálogo asignar/reasignar ────────────────────────────── */
function DialogoAsignar({ item, choferes = [], choferesEnUso = new Set(), onClose, onOk }) {
  // Chofer previamente asignado (nuevo modelo) o legado de pipa
  const choferPrevId   = item.chofer_asignado_id   || "";
  const choferPrevNom  = item.chofer_asignado_nombre || "";
  const pipaPrevId     = item.pipa_asignada_id || "";

  const [choferId, setChoferId] = useState(choferPrevId);
  const hoy    = new Date().toISOString().slice(0, 10);
  const mañana = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [fecha, setFecha]               = useState(item.fecha_programada || mañana);
  const [fechaAtencion, setFechaAtencion] = useState(item.fecha_atencion || "");

  const URG_VALIDA = new Set(["URGENTE", "NORMAL", "BAJA"]);
  const _urgInicial = URG_VALIDA.has(String(item.urgencia || "").toUpperCase())
    ? item.urgencia : "NORMAL";
  const [urgencia, setUrgencia] = useState(_urgInicial);
  const [busy, setBusy] = useState(false);

  const errorAString = (e) => {
    try {
      const det = e?.response?.data?.detail;
      if (typeof det === "string") return det;
      if (Array.isArray(det))
        return det.map(it => `${Array.isArray(it?.loc) ? it.loc.slice(-1)[0] + ": " : ""}${it?.msg || JSON.stringify(it)}`).join(" · ");
      if (det && typeof det === "object") return JSON.stringify(det);
      return e?.message || "Error al asignar";
    } catch { return "Error al asignar"; }
  };

  const choferSeleccionado = choferes.find(c => c.id === choferId);

  const enviar = async () => {
    if (!choferId || !fecha) return;
    const urgFinal = URG_VALIDA.has(String(urgencia || "").toUpperCase()) ? urgencia : "NORMAL";
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bandeja-pipas/asignar`, {
        origen: item.origen,
        doc_id: item.id,
        chofer_id: choferId,
        chofer_nombre: choferSeleccionado?.nombre || "",
        fecha_programada: fecha,
        fecha_atencion: fechaAtencion || null,
        urgencia: urgFinal,
      });
      const verb = r.data.reasignacion ? "reasignado" : "asignado";
      const urgTxt = urgFinal === "URGENTE" ? " · URGENTE 🔥" : urgFinal === "BAJA" ? " · BAJA" : "";
      toast.success(`${item.folio} ${verb} a ${choferSeleccionado?.nombre || choferId} para ${fecha}${urgTxt}`);
      onOk();
    } catch (e) {
      toast.error(errorAString(e));
    } finally {
      setBusy(false);
    }
  };

  const yaAsignado = choferPrevId || pipaPrevId;
  const om = ORIGEN_META[item.origen] || ORIGEN_META.CRC;

  return (
    <div className="mp-bg" onClick={onClose} data-testid="dlg-asignar">
      <div className="mp-card" onClick={(e) => e.stopPropagation()} style={{maxWidth: 460}}>
        <header className="mp-head">
          <div className="mp-head-text">
            <h2 className="mp-title">
              <i className="fa-solid fa-user-check mr-2"/>
              {yaAsignado ? "Reasignar chofer" : "Asignar chofer"}
            </h2>
            <div className="mp-id">
              <span className="origen-pill mr-2" style={{background: om.bg, color: om.color}}>{om.label}</span>
              {item.folio}
            </div>
          </div>
          <button type="button" onClick={onClose} className="mp-x" data-testid="btn-cerrar-dlg">
            <i className="fa-solid fa-xmark"/>
          </button>
        </header>

        <div className="mp-body" style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
          {/* Aviso de reintento tras no atendido */}
          {item.estado === "NO_PUDO_ATENDER" && (
            <div className="text-xs p-3 rounded-lg" style={{background:"#FEF2F2", border:"1.5px solid #FCA5A5", color:"#991B1B"}} data-testid="aviso-no-atendido">
              <div className="font-bold mb-1"><i className="fa-solid fa-circle-xmark mr-1"/>Intento anterior fallido</div>
              {item.motivo_no_pudo && <div className="mb-1">Motivo: <strong>{item.motivo_no_pudo}</strong></div>}
              {item.fecha_no_pudo && <div className="opacity-80">Fecha: {fmtFecha(item.fecha_no_pudo)}</div>}
              <div className="mt-1 opacity-80">El historial de este intento quedará preservado en el sistema.</div>
            </div>
          )}
          {/* Aviso de reasignación normal */}
          {yaAsignado && item.estado !== "NO_PUDO_ATENDER" && (
            <div className="text-xs bg-amber-50 text-amber-800 p-2 rounded" data-testid="aviso-reasignar">
              <i className="fa-solid fa-triangle-exclamation mr-1"/>
              {choferPrevNom
                ? <>Ya asignado a <strong>{choferPrevNom}</strong>.</>
                : <>Ya asignado a pipa <strong>{pipaPrevId}</strong> (registro anterior).</>
              }
              {item.fecha_programada && <> Fecha previa: {item.fecha_programada}.</>}
            </div>
          )}

          {/* Selector de chofer */}
          <div>
            <label className="mp-validar-lbl">
              <i className="fa-solid fa-user mr-1"/>Chofer *
            </label>
            <select
              className="mp-select"
              value={choferId}
              onChange={(e) => setChoferId(e.target.value)}
              data-testid="select-chofer"
            >
              <option value="">— Selecciona chofer —</option>
              {choferes.map(c => {
                const yaAsig = choferesEnUso.has(c.id) && c.id !== choferPrevId;
                return (
                  <option key={c.id} value={c.id}>
                    {c.nombre}{yaAsig ? " · (tiene asignación abierta)" : ""}
                  </option>
                );
              })}
            </select>
            {choferesEnUso.size > 0 && (
              <div className="text-[11px] text-slate-500 mt-1">
                <i className="fa-solid fa-circle-info mr-1"/>
                "(tiene asignación abierta)" es solo informativo — puedes asignarles más de un CRC.
              </div>
            )}
          </div>

          {/* Preview del chofer seleccionado */}
          {choferSeleccionado && (
            <div style={{
              background: "#F0FDF4", border: "1px solid #86EFAC",
              borderRadius: 8, padding: "8px 12px",
              display: "flex", alignItems: "center", gap: 8,
            }} data-testid="preview-chofer">
              <div style={{
                width: 32, height: 32, borderRadius: "50%",
                background: "#16A34A", color: "white",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 800, fontSize: 12, flexShrink: 0,
              }}>
                {choferSeleccionado.nombre.split(" ").map(p => p[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div>
                <div style={{fontWeight: 700, fontSize: 13, color: "#15803D"}}>
                  {choferSeleccionado.nombre}
                </div>
                <div style={{fontSize: 11, color: "#4ADE80"}}>
                  {choferSeleccionado.telefono
                    ? <><i className="fa-solid fa-message mr-1"/>Recibirá notificación por WhatsApp</>
                    : <><i className="fa-solid fa-triangle-exclamation mr-1"/>Sin teléfono registrado — no recibirá WhatsApp</>
                  }
                </div>
              </div>
            </div>
          )}

          {/* Fecha programada */}
          <div>
            <label className="mp-validar-lbl">Fecha programada *</label>
            <input type="date" className="mp-select" value={fecha}
                   onChange={(e) => setFecha(e.target.value)} data-testid="input-fecha"/>
          </div>

          {/* Día específico de atención */}
          <div>
            <label className="mp-validar-lbl">Día específico de atención (opcional)</label>
            <input type="date" className="mp-select" value={fechaAtencion} min={hoy}
                   onChange={(e) => setFechaAtencion(e.target.value)} data-testid="input-fecha-atencion"/>
            <div className="text-[11px] text-slate-500 mt-1">Si lo dejas vacío, el chofer no verá fecha límite.</div>
          </div>

          {/* Urgencia */}
          <div>
            <label className="mp-validar-lbl">Urgencia *</label>
            <select className="mp-select" value={urgencia}
                    onChange={(e) => setUrgencia(e.target.value)} data-testid="select-urgencia">
              <option value="URGENTE">🔥 URGENTE — atender hoy o mañana</option>
              <option value="NORMAL">NORMAL</option>
              <option value="BAJA">BAJA</option>
            </select>
          </div>
        </div>

        <footer className="mp-foot">
          <button
            className="btn-primary"
            disabled={!choferId || !fecha || busy}
            onClick={enviar}
            data-testid="btn-confirm-asignar"
          >
            {busy ? "Guardando…" : (yaAsignado ? "Confirmar reasignación" : "Confirmar asignación")}
          </button>
          <button className="btn-ghost" onClick={onClose} style={{padding:"0.55rem 1rem"}}>
            Cancelar
          </button>
        </footer>
      </div>
    </div>
  );
}
